import { useEffect } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { PrimaryButton } from "@/components/brand";
import { useLang, t, withLang } from "@/lib/i18n";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const SECTIONS = [
  {
    index: "01",
    title: { en: "How Routa works", fr: "Comment Routa fonctionne" },
    items: [
      {
        q: { en: "What is Routa?", fr: "Qu'est-ce que Routa?" },
        a: {
          en: "Routa is an AI-powered last-mile delivery platform for Vancouver-area businesses. Merchants dispatch orders from a web console, drivers pick them up in their PWA, and customers follow every leg of the journey through a shareable tracking link.",
          fr: "Routa est une plateforme de livraison du dernier kilomètre propulsée par l'IA, conçue pour les entreprises de la région de Vancouver. Les marchands répartissent leurs commandes depuis une console web, les livreurs les prennent en charge dans leur PWA, et les clients suivent chaque étape via un lien de suivi partageable.",
        },
      },
      {
        q: { en: "Where is Routa available?", fr: "Où Routa est-il offert?" },
        a: {
          en: "Metro Vancouver — Downtown Vancouver, Burnaby, Richmond, Surrey, North Vancouver, and Coquitlam. If you're outside those regions and would like coverage, get in touch and we'll add you to the waitlist.",
          fr: "Grand Vancouver — centre-ville de Vancouver, Burnaby, Richmond, Surrey, North Vancouver et Coquitlam. Si vous êtes en dehors de ces régions et souhaitez être couvert, écrivez-nous et nous vous ajouterons à la liste d'attente.",
        },
      },
      {
        q: { en: "Do I need to install an app?", fr: "Dois-je installer une application?" },
        a: {
          en: "No. Routa is a web app. Drivers and merchants can install it to their home screen from Chrome (Android) or Safari (iPhone) to get one-tap access and push notifications, but there's nothing to download from an app store.",
          fr: "Non. Routa est une application web. Livreurs et marchands peuvent l'ajouter à leur écran d'accueil depuis Chrome (Android) ou Safari (iPhone) pour un accès en un toucher et des notifications, mais il n'y a rien à télécharger d'un magasin d'applications.",
        },
      },
    ],
  },
  {
    index: "02",
    title: { en: "Merchant onboarding", fr: "Intégration des marchands" },
    items: [
      {
        q: { en: "How do I sign up as a merchant?", fr: "Comment m'inscrire comme marchand?" },
        a: {
          en: "Head to the sign-up page, pick a plan, and enter your business details. Every new merchant account starts in a Pending state so our operations team can verify your business before you dispatch your first delivery.",
          fr: "Rendez-vous sur la page d'inscription, choisissez un forfait et entrez les informations de votre entreprise. Chaque nouveau compte marchand démarre à l'état « en attente » afin que notre équipe des opérations puisse vérifier votre entreprise avant votre première livraison.",
        },
      },
      {
        q: { en: "Why is my merchant account showing 'Pending Approval'?", fr: "Pourquoi mon compte marchand affiche-t-il « En attente d'approbation »?" },
        a: {
          en: "That means our ops team hasn't reviewed your application yet. Approvals usually happen within one business day. You'll get an email the moment your account is approved, and the pending banner will disappear from your dashboard.",
          fr: "Cela signifie que notre équipe n'a pas encore examiné votre demande. Les approbations se font habituellement en un jour ouvrable. Vous recevrez un courriel dès que votre compte est approuvé, et la bannière « en attente » disparaîtra de votre tableau de bord.",
        },
      },
      {
        q: { en: "Can I run Routa from multiple pickup locations?", fr: "Puis-je utiliser Routa avec plusieurs lieux de ramassage?" },
        a: {
          en: "Yes. From the Locations tab in your merchant console you can add every store, warehouse, or dark kitchen you dispatch from and pick the right one when you create a delivery.",
          fr: "Oui. Depuis l'onglet Lieux de votre console marchand, ajoutez chaque magasin, entrepôt ou cuisine fantôme d'où vous expédiez, puis choisissez le bon au moment de créer une livraison.",
        },
      },
    ],
  },
  {
    index: "03",
    title: { en: "Delivery operations", fr: "Opérations de livraison" },
    items: [
      {
        q: { en: "How do I create a delivery?", fr: "Comment créer une livraison?" },
        a: {
          en: "From your merchant console → New delivery. Enter the pickup and dropoff addresses, the package description, and — importantly — the customer's phone number so they receive SMS status updates and the live tracking link.",
          fr: "Depuis votre console marchand → Nouvelle livraison. Saisissez les adresses de ramassage et de livraison, la description du colis et — surtout — le numéro du client pour qu'il reçoive les mises à jour par SMS et le lien de suivi en direct.",
        },
      },
      {
        q: { en: "Will my customer get updates?", fr: "Mes clients recevront-ils des mises à jour?" },
        a: {
          en: "Yes. Every customer with a phone number on the order receives an SMS when the driver is en route and another when the delivery is completed, plus a live tracking link they can open any time before delivery.",
          fr: "Oui. Chaque client dont le numéro figure sur la commande reçoit un SMS lorsque le livreur est en route et un autre à la livraison, ainsi qu'un lien de suivi en direct consultable à tout moment.",
        },
      },
      {
        q: { en: "What happens if a delivery fails?", fr: "Que se passe-t-il si une livraison échoue?" },
        a: {
          en: "The driver marks the stop as an exception with a reason (recipient unreachable, wrong address, refused, etc.). The exception appears immediately in your dispatch view and the customer's tracking page so you can decide next steps.",
          fr: "Le livreur marque l'arrêt comme exception avec un motif (destinataire injoignable, mauvaise adresse, refus, etc.). L'exception apparaît immédiatement dans votre vue de répartition et sur la page de suivi du client afin que vous puissiez décider de la suite.",
        },
      },
    ],
  },
  {
    index: "04",
    title: { en: "Pricing", fr: "Tarifs" },
    items: [
      {
        q: { en: "How much does Routa cost?", fr: "Combien coûte Routa?" },
        a: {
          en: <>Three monthly plans — Starter, Growth, and Scale — each with a different amount of dispatch capacity and support included. Full breakdown on the <Link to="/pricing" className="text-orange-500 hover:underline">pricing page</Link>.</>,
          fr: <>Trois forfaits mensuels — Starter, Growth et Scale — avec chacun une capacité de répartition et un niveau de soutien différents. Détails complets sur la <Link to="/pricing?lang=fr" className="text-orange-500 hover:underline">page des tarifs</Link>.</>,
        },
      },
      {
        q: { en: "Do I pay per delivery on top of the plan?", fr: "Y a-t-il un tarif par livraison en plus du forfait?" },
        a: {
          en: "Each plan comes with a monthly delivery capacity and standard rate-cards for anything above it. See the pricing page for the specifics that apply to your plan.",
          fr: "Chaque forfait inclut une capacité mensuelle de livraisons et une grille tarifaire standard au-delà. Consultez la page des tarifs pour les détails applicables à votre forfait.",
        },
      },
      {
        q: { en: "Can I switch plans later?", fr: "Puis-je changer de forfait plus tard?" },
        a: {
          en: "Yes. Reach out through the contact form and we'll upgrade or downgrade your plan on your next billing cycle. There's no long-term commitment on Starter or Growth.",
          fr: "Oui. Écrivez-nous via le formulaire de contact et nous ajusterons votre forfait au prochain cycle de facturation. Aucun engagement à long terme sur Starter ou Growth.",
        },
      },
    ],
  },
  {
    index: "05",
    title: { en: "Route optimization", fr: "Optimisation des trajets" },
    items: [
      {
        q: { en: "How does Routa optimize routes?", fr: "Comment Routa optimise-t-il les trajets?" },
        a: {
          en: "Our routing engine looks at every active delivery, driver location, pickup and dropoff, time windows, and package details, then sequences the stops that get the most orders delivered fastest across your fleet.",
          fr: "Notre moteur de routage tient compte de chaque livraison active, de la position des livreurs, des ramassages et livraisons, des fenêtres horaires et des détails du colis, puis séquence les arrêts pour livrer le plus de commandes le plus rapidement possible sur toute votre flotte.",
        },
      },
      {
        q: { en: "Can dispatchers override the suggested driver?", fr: "Le répartiteur peut-il modifier le livreur suggéré?" },
        a: {
          en: "Yes. Dispatch surfaces the optimal driver for every new order, but a human dispatcher can always reassign — helpful when a driver is finishing a shift or you have a preferred operator for a specific customer.",
          fr: "Oui. La répartition propose le meilleur livreur pour chaque nouvelle commande, mais un répartiteur humain peut toujours réattribuer — utile lorsqu'un livreur termine son quart ou lorsqu'un client préfère un opérateur donné.",
        },
      },
      {
        q: { en: "Does route optimization respond to traffic and cancellations?", fr: "L'optimisation réagit-elle au trafic et aux annulations?" },
        a: {
          en: "Yes. If a delivery is cancelled, added, or a driver goes offline, the routes in flight are re-scored so the remaining stops stay as efficient as possible.",
          fr: "Oui. Si une livraison est annulée, ajoutée, ou si un livreur se déconnecte, les trajets en cours sont recalculés pour garder les arrêts restants aussi efficaces que possible.",
        },
      },
    ],
  },
  {
    index: "06",
    title: { en: "Driver workflows", fr: "Flux de travail des livreurs" },
    items: [
      {
        q: { en: "How do I apply to drive for Routa?", fr: "Comment postuler pour livrer avec Routa?" },
        a: {
          en: <>Fill out the driver application at <Link to="/apply" className="text-orange-500 hover:underline">/apply</Link>. You'll enter your licence, vehicle, and insurance details, upload the required documents, and consent to a background check.</>,
          fr: <>Remplissez la demande de livreur au <Link to="/apply?lang=fr" className="text-orange-500 hover:underline">/apply</Link>. Vous entrerez vos informations de permis, véhicule et assurance, téléverserez les documents requis et consentirez à une vérification des antécédents.</>,
        },
      },
      {
        q: { en: "What documents do I need to drive?", fr: "Quels documents me faut-il pour livrer?" },
        a: {
          en: "A valid BC driver's licence (Class 5 or Class 4), vehicle registration, and proof of active insurance. If your licence expires soon we'll let you know 21 days in advance so you have time to renew.",
          fr: "Un permis de conduire de la C.-B. valide (classe 5 ou classe 4), l'immatriculation du véhicule et une preuve d'assurance active. Si votre permis expire bientôt, nous vous préviendrons 21 jours à l'avance pour vous laisser le temps de renouveler.",
        },
      },
      {
        q: { en: "How do I know when I've been assigned a delivery?", fr: "Comment savoir quand une livraison m'est attribuée?" },
        a: {
          en: "You'll get a push notification on your PWA home-screen icon, an SMS, and an email — all fired the moment a dispatcher assigns you. Open the app to see the pickup, dropoff, package details, and an AI-optimized route.",
          fr: "Vous recevrez une notification poussée sur l'icône PWA de votre écran d'accueil, un SMS et un courriel — tous envoyés dès qu'un répartiteur vous attribue la commande. Ouvrez l'application pour voir le ramassage, la livraison, les détails du colis et un trajet optimisé par l'IA.",
        },
      },
    ],
  },
];

export default function FAQ() {
  const lang = useLang();
  useEffect(() => {
    document.title = lang === "fr" ? "Routa · Foire aux questions" : "Routa · Frequently asked questions";
  }, [lang]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="faq-page">
      <MarketingNav />

      <section className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 pt-14 pb-8 lg:pt-24 lg:pb-14">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[00] FAQ</span>
          <span className="h-px w-16 bg-gray-300" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Vancouver · Canada</span>
        </div>
        <h1 className="headline text-4xl sm:text-5xl lg:text-6xl text-[#0a0a0a] max-w-3xl">
          {t(lang, {
            en: <>Questions, <span className="text-orange-500">answered</span>.</>,
            fr: <>Vos questions, <span className="text-orange-500">nos réponses</span>.</>,
          })}
        </h1>
        <p className="mt-6 text-base text-gray-500 max-w-xl leading-relaxed">
          {t(lang, {
            en: "Everything merchants, drivers, and dispatchers ask us most — from onboarding and pricing to route optimization and proof of delivery.",
            fr: "Tout ce que marchands, livreurs et répartiteurs nous demandent le plus — de l'intégration aux tarifs, du routage à la preuve de livraison.",
          })}
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 pb-16">
        <div className="grid gap-14 lg:gap-20">
          {SECTIONS.map((section) => (
            <div key={section.index} data-testid={`faq-section-${section.index}`}>
              <div className="grid lg:grid-cols-[220px_1fr] gap-4 lg:gap-14">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-2">[{section.index}]</div>
                  <div className="text-2xl font-bold tracking-tight text-[#0a0a0a] leading-tight">{t(lang, section.title)}</div>
                </div>
                <Accordion type="multiple" className="w-full">
                  {section.items.map((item, i) => (
                    <AccordionItem key={i} value={`${section.index}-${i}`} data-testid={`faq-q-${section.index}-${i}`}>
                      <AccordionTrigger className="text-base font-semibold text-[#0a0a0a] py-5">
                        {t(lang, item.q)}
                      </AccordionTrigger>
                      <AccordionContent className="text-[15px] text-gray-600 leading-relaxed pb-5">
                        {t(lang, item.a)}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 border border-gray-200 p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="faq-still-stuck">
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-3">
              {t(lang, { en: "[07] Still stuck?", fr: "[07] Encore bloqué?" })}
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-[#0a0a0a] max-w-xl">
              {t(lang, { en: "Talk to a real human on our ops team.", fr: "Parlez à une vraie personne de notre équipe des opérations." })}
            </h2>
            <p className="mt-2 text-sm text-gray-500 max-w-xl">
              {t(lang, { en: "We answer every message within one business day.", fr: "Nous répondons à chaque message en un jour ouvrable." })}
            </p>
          </div>
          <Link to={withLang("/contact", lang)}>
            <PrimaryButton orange arrow data-testid="faq-contact-cta">
              {t(lang, { en: "Contact us", fr: "Nous joindre" })}
            </PrimaryButton>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
